import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ROLES, ROLE_LABELS } from '@/lib/roles';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mail, Copy, Check } from 'lucide-react';

export const SUPPORT_EMAIL = 'assistenza.eto@hqe.it';

/**
 * Contenuto di orientamento per contesto:
 *  - context = 'login'  -> istruzioni primo accesso (utente non autenticato)
 *  - context = ruolo    -> istruzioni operative per il ruolo (Fastweb distingue
 *    ETO / territoriale tramite il flag isEto del profilo)
 */
function helpParagraphs(context, isEto) {
  if (context === 'login') {
    return [
      { title: 'Primo accesso?', text: "L'accesso è su invito: usa l'indirizzo email su cui hai ricevuto l'invito." },
      { title: 'Se usi un account Google', text: 'Con quell\'indirizzo è sufficiente cliccare "Accedi con Google".' },
      { title: 'Se NON usi Google', text: 'Al primo accesso clicca "Registrati", inserisci l\'email dell\'invito e scegli tu una password (non ti è stata assegnata: la crei in quel momento). Dagli accessi successivi userai email e password scelte.' },
      { title: 'Password dimenticata?', text: 'Usa il link sotto il form di accesso: riceverai un\'email per reimpostarla.' },
    ];
  }
  if (context === ROLES.FASTWEB) {
    return isEto
      ? [
          { title: 'Il tuo profilo (ETO)', text: 'Gestisci le pratiche: puoi prenderle in carico (azione irreversibile: HQ viene esclusa dal controllo documenti) e approvare o rifiutare i documenti delle pratiche in carico a Fastweb o dei professionisti interni HQ.' },
        ]
      : [
          { title: 'Il tuo profilo', text: 'Vedi le lavorazioni delle tue aree di competenza, in sola consultazione. Non puoi modificare le pratiche né approvarle: la gestione operativa è del team ETO.' },
          { title: 'Non trovi una lavorazione?', text: 'Se una lavorazione che ti aspetti non compare, probabilmente è fuori dalle tue aree di competenza.' },
        ];
  }
  if (context === ROLES.HQ) {
    return [
      { title: 'Il tuo ruolo', text: 'Approvi o rifiuti i documenti delle lavorazioni.' },
      { title: 'Pratiche gestite da Fastweb', text: "Le pratiche con badge 'Interno HQ' o 'In carico Fastweb' sono controllate da Fastweb: su quelle non devi agire." },
    ];
  }
  if (context === ROLES.FORNITORE) {
    return [
      { title: 'Il tuo ruolo', text: "Crei le lavorazioni e carichi i documenti: la spunta 'Doc caricati' avvia la verifica." },
      { title: 'In caso di rifiuto', text: 'Se i documenti vengono rifiutati, correggi e riapri la pratica; se la Ditta rifiuta il preventivo, aggiorna l\'importo e reinvia.' },
    ];
  }
  if (context === ROLES.DITTA) {
    return [
      { title: 'Il tuo ruolo', text: 'Approvi o rifiuti il preventivo economico delle lavorazioni dopo l\'approvazione dei documenti. Il rifiuto richiede una nota per il professionista.' },
    ];
  }
  return [];
}

/**
 * Modal di aiuto condiviso tra LoginPage (context='login') e Layout (context=ruolo).
 * Footer fisso: recapito assistenza + mailto con oggetto precompilato per il triage.
 */
export default function HelpModal({ open, onClose, context, userEmail, isEto }) {
  const location = useLocation();
  const paragraphs = helpParagraphs(context, isEto);
  const [copied, setCopied] = useState(false);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // Clipboard API non disponibile: l'indirizzo resta selezionabile a mano
      console.error('Copy failed:', e);
    }
  };

  const mailtoHref = useMemo(() => {
    const who = userEmail || 'utente non autenticato';
    const roleLabel = context === 'login' ? 'accesso' : (ROLE_LABELS[context] || context);
    const subject = `ETO Support — richiesta assistenza (${who}, ${roleLabel}, pagina ${location.pathname})`;
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  }, [userEmail, context, location.pathname]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="help-modal">
        <DialogHeader>
          <DialogTitle>Serve aiuto?</DialogTitle>
          <DialogDescription>
            {context === 'login' ? 'Come accedere alla piattaforma' : 'Come funziona la piattaforma per il tuo profilo'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {paragraphs.map((p) => (
            <div key={p.title}>
              <p className="text-sm font-semibold text-fw-dark">{p.title}</p>
              <p className="text-sm text-fw-muted">{p.text}</p>
            </div>
          ))}
          <div className="border-t border-fw-border pt-3 mt-2">
            <p className="text-sm text-fw-dark">
              Per assistenza scrivi a{' '}
              <span className="font-semibold select-all">{SUPPORT_EMAIL}</span>
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Button
                variant="outline"
                onClick={copyAddress}
                className="border-fw-border"
                data-testid="help-copy-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2 text-green-600" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copiato!' : 'Copia indirizzo'}
              </Button>
              <Button
                asChild
                className="bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark"
                data-testid="help-mailto-btn"
              >
                <a href={mailtoHref}>
                  <Mail className="w-4 h-4 mr-2" />
                  Apri il programma di posta
                </a>
              </Button>
            </div>
            <p className="text-[11px] text-fw-subtle mt-1.5">
              Se "Apri il programma di posta" non fa nulla, il tuo PC non ha un client email configurato:
              usa "Copia indirizzo" e scrivi dalla tua webmail.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
